import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SESSION_STORAGE_VARIABLES, sessionStorageManager } from '@app/services/storageManager';
import { ROUTES } from '@app/router/constants';
import { AemFeature } from '@app/components/AdobeExperienceFragments';
import { CommonQuestions } from '@app/components/CommonQuestions';
import { MobileProfileButton } from '@app/components/MobileProfileButton';
import { LoadingModal } from '@app/components/Modal';
import { PageLayout } from '@app/components/PageLayout';
import { useNavigate, useShowAddonsShop } from '@app/hooks';
import { getAddOnOffers, refetchAddOnOffers, refetchCurrentAddOns, selectAddOns } from '@app/redux/addOns';
import { AlertType, removeAlert } from '@app/redux/alerts';
import { selectHasFiber, selectUser } from '@app/redux/auth';
import { fetchBillingInfo, FetchBillingInfoPayload } from '@app/redux/billingInfo';
import { selectConfig } from '@app/redux/config';
import { getContractInstance } from '@app/redux/electronicCustomerAgreement';
import { getUsage, selectCurrentUsage, selectPlanCharacteristics, selectShouldFetchUsage } from '@app/redux/plan';
import {
  getInstallationWorkOrder,
  refetchScheduleInstallation,
  selectScheduleInstallation,
} from '@app/redux/scheduleInstallation';
import { selectUtil, setOverviewPageStatistics } from '@app/redux/utils';
import { getWorkOrders, selectWorkOrders } from '@app/redux/workOrders';
import { useCommonQuestionsFeature } from '@app/utils/useCommonQuestionsFeature';

import { Platform } from '@mfe/shared/schema-types';

import { BuyMore, ModalContext, useDialogType } from '../AddDataBoost';
import { useShouldRefetchCurrentAddons } from '../ShopAddons/hooks';
import { OverviewArticlesSection } from './articles-section';
import AlertsSection from './components/AlertsSection';
import { BalanceCard } from './components/BalanceCard';
import { MyAddons } from './components/MyAddons';
import MyViasatAppAd from './components/MyViasatAppAd';
import { DataUsage } from './data-usage';
import { Header } from './header';
import { PlanPreview } from './plan-preview';

import styles from './styles.module.scss';

const Overview = ({
  navigateToPage,
}: {
  navigateToChangePlan?: () => void;
  navigateToPage?: (link: string) => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { goTo } = useNavigate();

  const {
    pageStats: {
      pages: {
        overview: { lastUpdatedTime },
      },
    },
  } = useSelector(selectUtil);

  const { platform, enableFiber } = useSelector(selectConfig);
  const hasFiberInternet = useSelector(selectHasFiber);

  const {
    characteristics: { id: productTypeId },
  } = useSelector(selectPlanCharacteristics);

  const {
    data: { monthlyDataCap },
  } = useSelector(selectCurrentUsage);

  const {
    user: {
      auth: { isAuthenticated },
    },
  } = useSelector(selectUser);

  const shouldShowDataUsage = useSelector(selectShouldFetchUsage);
  const hasNotFetchedUsage = productTypeId && !monthlyDataCap;

  const { workOrders } = useSelector(selectWorkOrders);
  const { addOnOrderId, submitOrder, configureAddon } = useSelector(selectAddOns);
  const { error, submitSuccess, availableDates } = useSelector(selectScheduleInstallation);

  const [dialogType, dialogTypeHandlers] = useDialogType();
  const { resetDialogType } = dialogTypeHandlers;
  const shouldRefetchCurrentAddOns = useShouldRefetchCurrentAddons();
  const showAddonsShop = useShowAddonsShop();

  useEffect(() => {
    if (enableFiber && hasFiberInternet) {
      dispatch(getContractInstance());
    }
    if (shouldRefetchCurrentAddOns) {
      dispatch(refetchCurrentAddOns(addOnOrderId as string));
    }
    dispatch(fetchBillingInfo(FetchBillingInfoPayload.OVERVIEW));
    if (workOrders === null) {
      dispatch(getWorkOrders());
    }
    return () => {
      dispatch(removeAlert(AlertType.ChangePlanSuccess));
    };
  }, [enableFiber, hasFiberInternet, shouldRefetchCurrentAddOns, addOnOrderId, dispatch]);

  useEffect(() => {
    if (!showAddonsShop) {
      return;
    }
    if (addOnOrderId || submitOrder.error || configureAddon) {
      // Refetch to get a new offerId for the cart of the next order submission
      dispatch(refetchAddOnOffers());
    } else {
      dispatch(getAddOnOffers());
    }
  }, [showAddonsShop, addOnOrderId, submitOrder.error, configureAddon, dispatch]);

  useEffect(() => {
    if (workOrders !== null && availableDates === null && !error) {
      dispatch(getInstallationWorkOrder());
    }
  }, [workOrders, error, dispatch]);

  useEffect(() => {
    if (submitSuccess) {
      dispatch(refetchScheduleInstallation());
    }
  }, [submitSuccess, dispatch]);

  useEffect(() => {
    if (!lastUpdatedTime && isAuthenticated) {
      dispatch(setOverviewPageStatistics());
    }
  }, [dispatch, lastUpdatedTime, isAuthenticated]);

  useEffect(() => {
    if (hasNotFetchedUsage && shouldShowDataUsage) {
      dispatch(getUsage());
    }
  }, [dispatch, hasNotFetchedUsage, shouldShowDataUsage]);

  const mobileAppModal =
    platform !== Platform.Web && dialogType !== null ? (
      <React.Suspense fallback={<LoadingModal isOpen={true} handleClose={() => resetDialogType()} />}>
        <BuyMore />
      </React.Suspense>
    ) : null;

  const handleManagePlan = () => {
    sessionStorageManager.setItem(SESSION_STORAGE_VARIABLES.SHOW_PLAN_TAB, true);
    goTo(ROUTES.PROFILE);
  };

  const handleNavigateToPage = (link: string) => {
    if (navigateToPage) {
      navigateToPage(link);
    } else {
      goTo(link);
    }
  };

  const handleViewDataUsage = () => goTo(ROUTES.PLAN);

  const overviewCommonQuestionsFeature = useCommonQuestionsFeature({
    fiber: AemFeature.OVERVIEW_COMMON_QUESTIONS_FIBER,
    default: AemFeature.OVERVIEW_COMMON_QUESTIONS,
  });

  const overviewPageContent = (
    <>
      <MobileProfileButton />
      <PageLayout>
        <AlertsSection navigateToPage={handleNavigateToPage} />
        <Header />
        <div data-cy="overview-body" className={styles['content']}>
          <div className={styles['balance']}>
            <BalanceCard />
          </div>
          <div className={styles['questions']}>
            <div className={styles['commonQuestionsMargin']}>
              <CommonQuestions feature={overviewCommonQuestionsFeature} />
            </div>
          </div>
          {platform === Platform.Web && (
            <div className={styles['mvAppAd']}>
              <MyViasatAppAd />
            </div>
          )}
          <div className={styles['infoContainer']}>
            <PlanPreview handleManagePlan={handleManagePlan} handleViewDataUsage={handleViewDataUsage} />
            {shouldShowDataUsage && <DataUsage handleViewDetailedUsage={handleViewDataUsage} />}
            <MyAddons />
            <OverviewArticlesSection />
          </div>
        </div>
      </PageLayout>
    </>
  );

  return (
    <ModalContext.Provider value={{ dialogType, dialogTypeHandlers }}>
      {mobileAppModal ?? overviewPageContent}
    </ModalContext.Provider>
  );
};

export default Overview;
